import { useState } from 'react'
import { useFieldArray, useFormContext, useWatch, type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import {
	Button,
	FormControl, FormField, FormItem, FormLabel, FormMessage,
	IconButton,
	Input, Label,
	RadioGroup,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Switch,
} from '@rentflowdev/ui'
import { splitVat } from '@rentflowdev/common/vat'
import { useGetServiceVatRateQuery } from '@/api'
import type { ServiceProvisionType } from '@/types'
import { SectionHeading } from './section-heading'
import { RadioOption } from './form-options'
import { type AddPropertyFormValues } from './schema'
import { useAccountCurrency } from '@/lib/use-account-currency'

/** Sentinel for "none of the presets" — the stored name is then free text. */
const OTHER = '__OTHER__'

const SERVICE_NAME_OPTIONS = [
	{ value: 'Паркинг',  labelKey: 'properties.services.serviceNames.parking' },
	{ value: 'Уборка',   labelKey: 'properties.services.serviceNames.cleaning' },
	{ value: 'Интернет', labelKey: 'properties.services.serviceNames.internet' },
	{ value: 'Кладовая', labelKey: 'properties.services.serviceNames.storage' },
	{ value: 'Охрана',   labelKey: 'properties.services.serviceNames.security' },
] as const

const PROVISION_OPTIONS: readonly ServiceProvisionType[] = ['OWN', 'AGENCY']

const UNIT_OPTIONS = [
	{ value: 'место', labelKey: 'properties.services.units.spot' },
	{ value: 'кв.м',  labelKey: 'properties.services.units.sqm' },
	{ value: 'час',   labelKey: 'properties.services.units.hour' },
	{ value: 'день',  labelKey: 'properties.services.units.day' },
	{ value: 'месяц', labelKey: 'properties.services.units.month' },
	{ value: 'шт',    labelKey: 'properties.services.units.piece' },
] as const

const fmt = (value: number) =>
	value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * The rate for one service, and the only choice left to the user.
 *
 * The rate is resolved server-side from the provision type and the account's
 * regime — the matrix has one home, and a copy in the browser would drift.
 * Which endpoint is asked matters: a service is not the premises, so the
 * property rate would return the residential exemption and charge 0% on
 * cleaning attached to a flat.
 */
function ServiceVat({
	control,
	index,
}: {
	control: Control<AddPropertyFormValues>
	index: number
}) {
	const { t } = useTranslation()
	const provisionType =
		(useWatch({ control, name: `additionalServices.${index}.provisionType` }) as
			| ServiceProvisionType
			| undefined) ?? 'OWN'
	const price = useWatch({ control, name: `additionalServices.${index}.pricePerUnit` })
	const quantity = useWatch({ control, name: `additionalServices.${index}.quantity` })

	const { data: decision } = useGetServiceVatRateQuery({ provisionType })

	if (!decision) return null

	if (decision.kind !== 'rate') {
		// `forbidden` cannot happen for a service — НПД may sell one — so the
		// only way here is an unset regime, which the rent-terms step asks for.
		return (
			<p className="text-sm text-(--color-muted-foreground)">
				{t(`properties.rentTerms.vat.${decision.code}`)}
			</p>
		)
	}

	const { percent, toggleable, hint } = decision
	// Follows what is typed rather than what is saved, so the sum moves with
	// the field. NaN before anything is entered is coerced to zero.
	const base = (Number(price) || 0) * (Number(quantity) || 0)

	return (
		<FormField
			control={control}
			name={`additionalServices.${index}.vatIncluded`}
			render={({ field }) => {
				const included = field.value ?? true
				const split = splitVat({ amount: base.toFixed(2), percent, included })
				return (
					<FormItem className="flex flex-col gap-2 space-y-0 rounded-(--radius-md) border border-(--color-border) p-3">
						{percent > 0 && (
							<p className="text-sm font-[500] text-(--color-foreground)">
								{/* Only once there is something to charge it on. An
								    untouched row used to read «В т.ч. НДС 22%: 0,00 ₽»,
								    stating a computed sum where nothing was entered. */}
								{base === 0
									? t('properties.rentTerms.vat.rateOnly', { percent })
									: included
										? t('properties.rentTerms.vat.breakdownIncluded', {
												percent,
												vat: fmt(Number(split.vat)),
											})
										: t('properties.rentTerms.vat.breakdownOnTop', {
												percent,
												vat: fmt(Number(split.vat)),
												total: fmt(Number(split.gross)),
											})}
							</p>
						)}
						{/* Which rule produced the rate, in the user's terms. The matrix
						    decides this — four regimes share one legal ground but not one
						    explanation, and picking the text here from the regime would
						    put a copy of those branches in the browser. */}
						<p
							className={
								percent === 0
									? 'text-sm font-[500] text-(--color-foreground)'
									: 'text-xs text-(--color-muted-foreground)'
							}
						>
							{t(`properties.rentTerms.vat.hints.${hint}`, { percent })}
						</p>
						{/* At 0% there is nothing to sit inside the price or on top of
						    it, so the choice is not offered — matching the rate block
						    on the rent-terms step. */}
						{toggleable && (
							<label className="flex items-center justify-between gap-3">
								<span className="text-sm">
									{included
										? t('properties.rentTerms.vat.includedInPrice')
										: t('properties.rentTerms.vat.addedOnTop')}
								</span>
								<FormControl>
									<Switch checked={included} onCheckedChange={field.onChange} />
								</FormControl>
							</label>
						)}
					</FormItem>
				)
			}}
		/>
	)
}

function ServiceRow({
	control,
	index,
	onRemove,
}: {
	control: Control<AddPropertyFormValues>
	index: number
	onRemove: () => void
}) {
	const { t } = useTranslation()
	const accountCurrency = useAccountCurrency()
	const name = useWatch({ control, name: `additionalServices.${index}.name` })
	// A name that matches no preset is a custom one — which is also how an
	// existing service edits back into the form without losing its title.
	const [custom, setCustom] = useState(
		() => !!name && !SERVICE_NAME_OPTIONS.some((o) => o.value === name),
	)

	return (
		<section className="flex flex-col gap-5">
			<div className="flex items-center gap-2 py-4">
				<SectionHeading className="py-0">
					{t('properties.services.serviceNumber', { num: index + 1 })}
				</SectionHeading>
				<IconButton
					variant="secondary"
					size="sm"
					onClick={onRemove}
					aria-label={t('properties.services.removeService')}
				>
					<Trash2Icon />
				</IconButton>
			</div>

			<FormField
				control={control}
				name={`additionalServices.${index}.name`}
				render={({ field }) => (
					<FormItem className="w-1/2">
						<Select
							value={custom ? OTHER : field.value}
							onValueChange={(v) => {
								setCustom(v === OTHER)
								// Cleared on the way into free text so the preset that
								// was showing is not submitted as the typed name.
								field.onChange(v === OTHER ? '' : v)
							}}
						>
							<FormControl>
								<SelectTrigger className="w-full">
									<SelectValue placeholder={t('properties.services.selectService')} />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{SERVICE_NAME_OPTIONS.map((opt) => (
									<SelectItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</SelectItem>
								))}
								<SelectItem value={OTHER}>
									{t('properties.services.serviceNames.other')}
								</SelectItem>
							</SelectContent>
						</Select>
						{custom && (
							<Input
								className="mt-2"
								placeholder={t('properties.services.customName')}
								value={field.value ?? ''}
								onChange={(e) => field.onChange(e.target.value)}
							/>
						)}
						<FormMessage />
					</FormItem>
				)}
			/>

			{/* Decides the rate before any regime is consulted: a re-invoice
			    without a mark-up is a transit payment, so no VAT arises on it
			    even on ОСНО. */}
			<FormField
				control={control}
				name={`additionalServices.${index}.provisionType`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t('properties.services.provisionType')}</FormLabel>
						<FormControl>
							<RadioGroup
								value={field.value ?? 'OWN'}
								onValueChange={field.onChange}
								name={field.name}
								className="flex flex-wrap gap-2"
							>
								{PROVISION_OPTIONS.map((value) => (
									<RadioOption
										key={value}
										value={value}
										id={`service-${index}-provision-${value}`}
										label={t(`properties.services.provisionTypes.${value}`)}
									/>
								))}
							</RadioGroup>
						</FormControl>
						<p className="text-xs text-(--color-muted-foreground)">
							{t(`properties.services.provisionHints.${field.value ?? 'OWN'}`)}
						</p>
						<FormMessage />
					</FormItem>
				)}
			/>

			<div className="flex gap-2 items-start">
				<div className="flex-1 flex flex-col gap-2">
					<Label className="text-sm leading-5 font-[500] text-(--color-foreground)">
						{t('properties.services.priceAndCurrency')}
					</Label>
					{/* The field wraps the whole bordered group so its message has
					    somewhere to go: inside the group it sat under
					    `overflow-hidden` and a rejected value was silently dropped
					    with nothing on screen to say why. */}
					<FormField
						control={control}
						name={`additionalServices.${index}.pricePerUnit`}
						render={({ field }) => (
							<FormItem className="space-y-0">
								<div className="flex rounded-md border border-(--color-border) bg-(--color-background) overflow-hidden">
									<FormControl>
										<Input
											type="number"
											min={0}
											placeholder="3000"
											className="flex-1 border-0 rounded-none focus:ring-0 shadow-none"
											{...field}
											value={field.value ?? ''}
										/>
									</FormControl>
									<div className="w-px bg-(--color-border)" aria-hidden />
									{/* Every service on a property is priced in the account's
									    currency, so this states it rather than asking once per row. */}
									<span className="flex flex-1 items-center px-3 text-sm text-(--color-muted-foreground)">
										{t(`currencies.${accountCurrency}`)}
									</span>
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				<div className="w-[21.125rem] flex flex-col gap-2">
					<Label className="text-sm leading-5 font-[500] text-(--color-foreground)">
						{t('properties.services.quantityAndUnit')}
					</Label>
					{/* Same reason as the price above: the message needs to sit
					    outside the clipped group to be seen at all. */}
					<FormField
						control={control}
						name={`additionalServices.${index}.quantity`}
						render={({ field }) => (
							<FormItem className="space-y-0">
								<div className="flex rounded-md border border-(--color-border) bg-(--color-background) overflow-hidden">
									<FormControl>
										<Input
											type="number"
											min={0}
											placeholder="1"
											className="flex-1 border-0 rounded-none focus:ring-0 shadow-none"
											{...field}
											value={field.value ?? ''}
										/>
									</FormControl>
									<div className="w-px bg-(--color-border)" aria-hidden />
									<FormField
										control={control}
										name={`additionalServices.${index}.unit`}
										render={({ field: unitField }) => (
											<FormItem className="flex-1 space-y-0">
												<Select value={unitField.value} onValueChange={unitField.onChange}>
													<FormControl>
														<SelectTrigger className="border-0 rounded-none focus:ring-0 shadow-none">
															<SelectValue placeholder={t('properties.services.unitPlaceholder')} />
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{UNIT_OPTIONS.map((opt) => (
															<SelectItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</SelectItem>
														))}
													</SelectContent>
												</Select>
											</FormItem>
										)}
									/>
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
			</div>

			<ServiceVat control={control} index={index} />
		</section>
	)
}

export function Step5AdditionalServices() {
	const { t } = useTranslation()
	const accountCurrency = useAccountCurrency()
	const { control } = useFormContext<AddPropertyFormValues>()
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'additionalServices',
	})

	return (
		<div className="flex flex-col gap-5 px-4 pt-2 pb-8">
			{fields.map((field, index) => (
				<ServiceRow
					key={field.id}
					control={control}
					index={index}
					onRemove={() => remove(index)}
				/>
			))}

			<Button
				type="button"
				variant="outline"
				size="full"
				iconRight={<PlusIcon className="size-4" />}
				onClick={() =>
					append({ currency: accountCurrency, provisionType: 'OWN', vatIncluded: true })
				}
			>
				{t('properties.services.addService')}
			</Button>
		</div>
	)
}
